import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AlertTriangle, Check, CheckCircle2, Github, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";

interface TokenStatus {
  connected: boolean;
  scopes?: string[];
  createdAt?: string | null;
  valid?: boolean;
  login?: string | null;
}

interface OrgBuildToken extends TokenStatus {
  ownerEmail?: string;
  isYou?: boolean;
}

/**
 * Org settings → GitHub. Builds clone with the PAT of the org owner (see
 * orgOwnerPat() in apps/worker/src/jobs/build.ts), so the top card shows
 * which token that is and whether GitHub still accepts it. The bottom card
 * manages the signed-in user's own PAT — tokens are per user, so replacing
 * the build token means the owner saving a new one here.
 */
export function GitHubToken() {
  const { org } = useParams<{ org: string }>();
  const qc = useQueryClient();

  const build = useQuery({
    queryKey: ["org-github-token", org],
    queryFn: () => api.get<OrgBuildToken>(`/orgs/${org}/github-token`),
  });
  const mine = useQuery({
    queryKey: ["github-token", "verified"],
    queryFn: () => api.get<TokenStatus>("/github/token?verify=1"),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["org-github-token", org] });
    qc.invalidateQueries({ queryKey: ["github-token"] });
    qc.invalidateQueries({ queryKey: ["github-repos"] });
  }

  const [pat, setPat] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.post<{ login: string; scopes: string[] }>("/github/token", { pat }),
    onSuccess: (r) => {
      setPat("");
      setSaved(r.login);
      setTimeout(() => setSaved(null), 4000);
      refresh();
    },
  });

  const disconnect = useMutation({
    mutationFn: () => api.delete("/github/token"),
    onSuccess: refresh,
  });

  if (build.isLoading || mine.isLoading) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground inline-flex items-center justify-center w-full gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking tokens with GitHub…
      </div>
    );
  }

  const b = build.data;
  const m = mine.data;

  return (
    <div className="max-w-2xl space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Build token</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p className="text-xs text-muted-foreground">
            Deployments in this organization clone repositories with the GitHub token of an org owner.
          </p>
          {!b?.connected ? (
            <Notice>
              No org owner has connected a GitHub token. Private repositories can't be built until an
              owner connects one below.
            </Notice>
          ) : (
            <>
              <TokenRow
                status={b}
                who={b.isYou ? `${b.ownerEmail} (you)` : (b.ownerEmail ?? "org owner")}
              />
              {!b.valid && (
                <Notice>
                  GitHub rejected this token. It has probably expired or been revoked, so builds of
                  private repositories will fail.{" "}
                  {b.isYou ? "Save a new token below." : `Ask ${b.ownerEmail} to save a new token in this page.`}
                </Notice>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your GitHub token</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {m?.connected ? (
            <div className="flex items-start justify-between gap-3">
              <TokenRow status={m} who="You" />
              <Button
                size="sm"
                variant="ghost"
                disabled={disconnect.isPending}
                onClick={() => {
                  if (confirm("Disconnect your GitHub token? Builds that rely on it will lose access to private repos."))
                    disconnect.mutate();
                }}
              >
                {disconnect.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Disconnect
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">You haven't connected a GitHub token.</p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="gh-pat">{m?.connected ? "Replace token" : "Connect token"}</Label>
            <Input
              id="gh-pat"
              type="password"
              autoComplete="off"
              placeholder="ghp_… or github_pat_…"
              value={pat}
              onChange={(e) => setPat(e.target.value)}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Needs the <span className="font-mono">repo</span> scope for private repositories and{" "}
              <span className="font-mono">admin:repo_hook</span> for push webhooks. The token is checked with
              GitHub before it replaces the current one.
            </p>
          </div>

          {save.error && (
            <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
              {save.error instanceof ApiError ? save.error.message : "Failed to verify token"}
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            {saved && (
              <span className="text-xs text-success inline-flex items-center gap-1">
                <Check className="h-3.5 w-3.5" /> Saved — connected as {saved}
              </span>
            )}
            <Button variant="primary" disabled={pat.length < 10 || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Github className="h-3.5 w-3.5" />}
              {m?.connected ? "Replace token" : "Connect"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TokenRow({ status, who }: { status: TokenStatus; who: string }) {
  return (
    <div className="space-y-1 text-sm">
      <div className="flex items-center gap-1.5">
        {status.valid ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-success" />
        ) : (
          <AlertTriangle className="h-3.5 w-3.5 text-danger" />
        )}
        <span>{who}</span>
        {status.login && (
          <span className="text-muted-foreground">
            · GitHub <span className="font-mono">@{status.login}</span>
          </span>
        )}
        {status.valid === false && <span className="text-danger text-xs">· rejected by GitHub</span>}
      </div>
      <div className="text-xs text-muted-foreground">
        Scopes: <span className="font-mono">{status.scopes?.length ? status.scopes.join(", ") : "none (fine-grained token)"}</span>
        {status.createdAt && <> · saved {new Date(status.createdAt).toLocaleDateString()}</>}
      </div>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5 flex gap-1.5">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
      <span>{children}</span>
    </div>
  );
}
